import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Briefcase, Home as HomeIcon, MoreHorizontal } from 'lucide-react';
import api from '../api/api';

export default function Register() {
  const [step, setStep] = useState('form');
  const [form, setForm] = useState({
    name: '',
    mail: '',
    mobile: '',
    address: '',
    area: '',
    division: '',
    landmark: '',
    pin: '',
    shortNameType: 'Home',
    shortNameOther: '',
    photoUrl: null,
  });
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const { setUser, setIsAuthenticated } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [lockPincode, setLockPincode] = useState(false);
  const [pincodeStatus, setPincodeStatus] = useState('');
  const [pincodeMessage, setPincodeMessage] = useState('');

  // 🆕 Area list and filter state
  const [areas, setAreas] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [showAreaList, setShowAreaList] = useState(false);
  const [selectedArea, setSelectedArea] = useState({ city: '', division: '' });
  const areaRef = useRef(null);

  useEffect(() => {
    const stateData = location.state || {};
    const storedDataRaw = sessionStorage.getItem('registrationPincodeGate');
    const storedData = storedDataRaw ? JSON.parse(storedDataRaw) : null;

    const verifiedPincode = stateData.verifiedPincode || storedData?.pincode || '';
    const status = stateData.pincodeStatus || storedData?.status || '';
    const message = stateData.pincodeMessage || storedData?.message || '';
    const shouldLock = stateData.lockPincode || !!verifiedPincode;

    if (!verifiedPincode) {
      navigate('/register', { replace: true });
      return;
    }

    setForm((prev) => ({ ...prev, pin: verifiedPincode }));
    setLockPincode(shouldLock);
    setPincodeStatus(status);
    setPincodeMessage(message);
  }, [location.state, navigate]);


  // 🆕 Fetch area list on mount
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const res = await api.GetCustAreaList();
        const list = res?.GetCustAreaListjs?.Table || [];
        setAreas(list);
        setFilteredAreas(list);
      } catch (err) {
        console.error('Failed to fetch area list', err);
      }
    };
    fetchAreas();
  }, []);

  // 🆕 Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (areaRef.current && !areaRef.current.contains(event.target)) {
        setShowAreaList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 🧠 Filter areas while typing (case-insensitive, search by city or division)
  const handleAreaChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, area: value }));
    setSelectedArea({ city: value, division: '' });

    if (!value.trim()) {
      setFilteredAreas(areas);
      setShowAreaList(false);
      return;
    }

    const filtered = areas.filter((a) => {
      const city = (a.city || '').toLowerCase();
      const division = (a.Division || '').toLowerCase();
      return city.includes(value.toLowerCase()) || division.includes(value.toLowerCase());
    });

    setFilteredAreas(filtered);
    setShowAreaList(true);
  };

  // 🆕 When area selected → also save division
  const handleSelectArea = (city, division) => {
    setSelectedArea({ city, division });
    setForm((prev) => ({ ...prev, area: city, division }));
    setShowAreaList(false);
  };


  const onFile = (e) => {
    const f = e.target.files[0];
    if (f) setForm((prev) => ({ ...prev, photoUrl: URL.createObjectURL(f) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 'form') {
      const { name, mail, mobile, address, area, landmark, pin, shortNameType, shortNameOther } = form;
      const shortName = shortNameType === 'Other' ? shortNameOther.trim() : shortNameType;

      if (!name || !mail || !mobile || !address || !area || !pin) {
        return setError('Please fill all required fields.');
      }
      if (!shortName) {
        return setError('Please enter address type.');
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
if (!emailRegex.test(mail)) {
  return setError('Enter a valid email address.');
}
      if (!/^\d{10}$/.test(mobile)) {
        return setError('Enter a valid 10-digit mobile number.');
      }
      if (!/^\d{6}$/.test(pin)) {
        return setError('Enter a valid 6-digit pincode.');
      }

      try {
        const payload = {
          Vcode: mobile,
          Vname: name,
          Mail: mail,
          Address1: address,
          Address2: landmark || '',
          Area: selectedArea.city,
          Division: selectedArea.division,
          ShortName: shortName,
          Pincode: pin, UserType: 'New User',
        };

        const res = await api.CustomerRegistrationApi(payload);
        const vid = res?.CustomerRegistrationApijs?.Table?.[0]?.Vid;
        const status = res?.CustomerRegistrationApijs?.Table?.[0]?.Status;
        const message = res?.CustomerRegistrationApijs?.Table?.[0]?.Message || res?.Message;
     const PinAllowed = res?.CustomerRegistrationApijs?.Table?.[0]?.PinAllowed || res?.PinAllowed;
        if (vid) {
          const userData = {
            Vid: vid, Vcode: mobile, Vname: name, name, mail, address, area: selectedArea.city,
            division: selectedArea.division, landmark, pin, mobile, UserType: 'New User',
            ShortName: shortName,
            PinAllowed: PinAllowed,
            companyAddress: "No.5/25, Balaji Street,Arumbakkam,Opp. Police Quarters, Taluk Office Road,Sri Venkatesa Perumal Nagar, Chennai -600106.",
            companyEmail: "annamatchennai@gmail.com",
            companyName: "Hitham Associates Private Limited",
            companyState: "TAMILNADU"
          };

          // Store in local storage
          localStorage.setItem('tempUser', JSON.stringify(userData));
          localStorage.setItem('otpSource', 'register');
          
          // Also store in session storage
          sessionStorage.setItem('tempUser', JSON.stringify(userData));
          
          navigate('/otpverify');
        } else if (status === 'Error' && message) {
          setError(message);
        } else if (message) {
          setError(message);
        } else {
          setError('Registration failed. Try again.');
        }
      } catch (err) {
        console.error(err);
        if (err.message.includes('500')) {
          setError('Duplicate mobile number. Registration failed.');
        } else {
          setError('Server error. Try again later.');
        }
      }
    } else {
     try {
       const userData = JSON.parse(localStorage.getItem('tempUser') || sessionStorage.getItem('tempUser') || 'null');
       if (!userData) return setError('Registration session expired. Please register again.');

       const verifyRes = await api.VerifyOtp(
         userData.Vcode,
         userData.UserType,
         otp,
         userData.Vid
       );
       const isVerified = verifyRes?.VerifyOtpjs?.Table?.[0]?.Status === 1;

       if (!isVerified) return setError('Invalid OTP.');

       navigate('/password');
     } catch (err) {
       console.error('OTP verification failed:', err);
       setError('Failed to verify OTP. Please try again.');
     }
   }
  };

  const handleBack = () => {
    if (step === 'otp') setStep('form');
    else navigate('/');
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 relative w-full max-w-3xl mx-auto">
      <button
        onClick={handleBack}
        className="absolute flex items-center gap-2 font-semibold text-gray-600 transition top-4 left-4 hover:text-red-700"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      <div className="w-full max-w-md p-8 mt-10 bg-white border-t-4 border-red-500 shadow-xl rounded-2xl md:max-w-3xl animate-fade-in">
        <h1 className="mb-2 text-3xl font-bold text-center text-red-600">
          Welcome to Annam Home Foods
        </h1>
        <h2 className="mb-6 text-xl font-semibold text-center text-gray-700">
          Create your account
        </h2>

        {/* {pincodeStatus && (
          <div className={`mb-4 p-3 rounded-lg border ${pincodeStatus === 'Allowed' ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
            <p className={`font-semibold ${pincodeStatus === 'Allowed' ? 'text-green-700' : 'text-yellow-700'}`}>
              Pincode Status: {pincodeStatus}
            </p>
            {pincodeMessage && <p className="mt-1 text-sm text-gray-700">{pincodeMessage}</p>}
          </div>
        )} */}

        <form onSubmit={submit} className="relative space-y-3">
          {step === 'form' ? (
            <>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full Name"
                className="w-full p-3 border rounded-md"
                required
              />
              <input
                value={form.mail}
                onChange={(e) => setForm({ ...form, mail: e.target.value })}
                type="mail"
                placeholder="Email ID"
                className="w-full p-3 border rounded-md"
                required
              />
              <input
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                type="tel"
                placeholder="10-digit Mobile Number"
                maxLength="10"
                className="w-full p-3 border rounded-md"
                required
              />
              <textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Delivery Address"
                className="w-full p-3 border rounded-md"
                required
              />

              {/* 🆕 Area dropdown with filter */}
              <div className="relative" ref={areaRef}>
                <input
                  type="text"
                  value={selectedArea.city}
                  onChange={handleAreaChange}
                  onFocus={() => setShowAreaList(true)}
                  placeholder="Search Area"
                  className="w-full p-3 border rounded-md"
                  required
                />
                {showAreaList && filteredAreas.length > 0 && (
                  <ul className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded-md shadow-lg max-h-40">
                    {filteredAreas.map((a, idx) => (
                      <li
                        key={idx}
                        onClick={() => handleSelectArea(a.city.trim(), a.Division)}
                        className="p-2 text-sm cursor-pointer hover:bg-red-100"
                      >
                        {a.city} <span className="text-xs text-gray-400">({a.Division})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <input
                value={form.landmark}
                onChange={(e) => setForm({ ...form, landmark: e.target.value })}
                placeholder="Landmark"
                className="w-full p-3 border rounded-md"
              />
              <input
                value={form.pin}
                onChange={(e) => {
                  if (lockPincode) return;
                  setForm({ ...form, pin: e.target.value });
                }}
                placeholder="Pincode"
                maxLength="6"
                className="w-full p-3 border rounded-md"
                disabled={lockPincode}
                required
              />
              {/* <input
                type="file"
                onChange={onFile}
                accept="image/*"
                className="w-full p-2 border rounded-md"
              /> */}
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Home', icon: HomeIcon },
                    { label: 'Office', icon: Briefcase },
                    { label: 'Other', icon: MoreHorizontal },
                  ].map(({ label, icon: Icon }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setForm({ ...form, shortNameType: label })}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold border rounded-full transition-colors ${
                        form.shortNameType === label
                          ? 'text-white bg-red-500 border-red-500'
                          : 'text-gray-700 bg-white border-gray-200 hover:bg-red-50 hover:border-red-200'
                      }`}
                    >
                      <Icon size={13} />
                      {label}
                    </button>
                  ))}
                </div>
                {form.shortNameType === 'Other' && (
                  <input
                    value={form.shortNameOther}
                    onChange={(e) => setForm({ ...form, shortNameOther: e.target.value })}
                    placeholder="Enter address type"
                    className="w-full p-3 border rounded-md"
                    required
                  />
                )}
              </div>
            </>
          ) : (
            <>
  <input
    value={otp}
    onChange={(e) => setOtp(e.target.value)}
    id="auth_otp"
    type="tel"
    placeholder="Enter OTP"
    className="w-full p-3 text-lg tracking-widest text-center border rounded-lg"
  />

  <p className="mt-2 text-xs text-center text-gray-500">
    Enter the OTP sent to your mobile.
  </p>
</>

          )}

          {error && <p className="text-sm text-red-500">{error}</p>}

          <button
            type="submit"
            className="w-full py-3 font-bold text-white bg-red-500 rounded-lg shadow hover:bg-red-600"
          >
            {step === 'form' ? 'Register' : 'Verify OTP'}
          </button>
        </form>
      </div>
    </div>
  );
}
